import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid } from 'recharts';
import { Flame } from 'lucide-react';

const has = (v) => v !== null && v !== undefined && v !== '';
const fmt = (v) => (has(v) ? Number(v).toLocaleString('es-ES') : '—');
const compact = (v) => {
  if (!has(v)) return '—';
  const n = Number(v);
  if (n >= 1e9) return `${(n / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1e4) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString('es-ES');
};

const MetricCard = ({ label, value, source, testId }) => (
  <div className="relative bg-white border border-teal-100 rounded-lg px-3 py-2 min-w-0" data-testid={testId}>
    {source && <span className="absolute bottom-1 right-1.5 text-[8px] text-slate-400">{source}</span>}
    <p className="text-[9px] font-semibold text-slate-500 uppercase leading-tight whitespace-nowrap overflow-hidden">{label}</p>
    <p className="text-lg font-bold text-teal-600 leading-tight">{value}</p>
  </div>
);

const InfoBox = ({ title, rows, testId }) => {
  const visible = rows.filter((r) => has(r.value));
  if (!visible.length) return null;
  return (
    <div className="border border-teal-200 rounded-lg overflow-hidden bg-white" data-testid={testId}>
      <div className="bg-primary text-white text-xs font-bold text-center py-1.5">{title}</div>
      <div className="p-2.5 space-y-1.5">
        {visible.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-slate-600">{r.label}</span>
            <span className="px-2 py-0.5 rounded-full border border-teal-200 bg-teal-50 text-teal-600 font-semibold text-[11px] truncate max-w-[110px]">{r.display ?? fmt(r.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SeoMetricsPanel = ({ metrics, history = [], website, language = 'es' }) => {
  if (!metrics) return null;
  const es = language === 'es';
  const m = metrics;
  const updated = m.updated_at ? String(m.updated_at).slice(0, 10) : null;
  const cards = [
    { k: 'dr', label: 'DR', value: fmt(m.dr), source: 'Ahrefs' },
    { k: 'traffic', label: es ? 'Tráfico' : 'Traffic', value: fmt(m.traffic) },
    { k: 'rd', label: 'RD', value: fmt(m.rd) },
    { k: 'top_10', label: '#10 Keywords', value: fmt(m.top_10) },
    { k: 'keywords', label: 'Keywords', value: fmt(m.keywords) },
    { k: 'tf', label: 'TF', value: fmt(m.tf), source: 'Majestic' },
    { k: 'cf', label: 'CF', value: fmt(m.cf), source: 'Majestic' },
    { k: 'ar', label: 'AR', value: compact(m.ar), source: 'Ahrefs' },
    { k: 'ur', label: 'UR', value: fmt(m.ur), source: 'Ahrefs' },
  ].filter((c) => has(m[c.k]));
  const positions = [
    { name: 'Top 3', value: m.top_3 },
    { name: 'Top 4-10', value: m.top_4_10 },
    { name: 'Top 11-20', value: m.top_11_20 },
    { name: 'Top 21-50', value: m.top_21_50 },
  ];
  const hasPositions = positions.some((p) => has(p.value));
  const points = (history || []).filter((p) => p && p.date);
  const totalKw = positions.reduce((s, p) => s + (Number(p.value) || 0), 0);

  return (
    <div className="rounded-xl border border-teal-200 overflow-hidden bg-teal-50/30" data-testid="seo-metrics-panel">
      <div className="bg-primary text-white px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center shrink-0"><Flame className="h-4 w-4" /></div>
          <div className="min-w-0">
            <p className="font-bold text-sm leading-tight">{es ? 'Métricas SEO' : 'SEO Metrics'}</p>
            <p className="text-[10px] text-teal-100 truncate">
              {m.title ? `${m.title} · ` : ''}{website}{updated ? ` · ${es ? 'actualizado' : 'updated'} ${updated}` : ''}
            </p>
          </div>
        </div>
        {m.topics?.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="seo-topics">
            {m.topics.slice(0, 4).map((t) => (
              <span key={t.name} className="text-[10px] font-semibold bg-white/15 border border-white/30 rounded px-1.5 py-0.5">
                {t.name}{has(t.value) ? ` ${t.value}` : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="p-3 space-y-3">
        {cards.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
            {cards.map((c) => <MetricCard key={c.k} label={c.label} value={c.value} source={c.source} testId={`seo-card-${c.k}`} />)}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {hasPositions && (
            <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="seo-positions-chart">
              <p className="text-xs font-bold text-slate-800">{es ? 'Keywords por posición en Google' : 'Keywords by Google position'}</p>
              <p className="text-[10px] text-slate-500 mb-2">{fmt(totalKw)} {es ? 'keywords posicionadas' : 'ranking keywords'}</p>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={positions.map((p) => ({ ...p, value: Number(p.value) || 0 }))} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} tickFormatter={compact} />
                    <Tooltip cursor={{ fill: '#eef4ff' }} />
                    <Bar dataKey="value" fill="#0046E8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="seo-traffic-chart">
            <p className="text-xs font-bold text-slate-800">{es ? 'Tráfico orgánico' : 'Organic traffic'}</p>
            <p className="text-[10px] text-slate-500 mb-2">{es ? 'País principal' : 'Main country'}: {m.top_location || '—'}</p>
            {points.length >= 2 ? (
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={points} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={compact} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Area type="monotone" dataKey="traffic" stroke="#0046E8" fill="#bfd3ff" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-36 rounded-lg bg-teal-50/60 flex flex-col items-center justify-center text-center px-4">
                <p className="text-2xl font-bold text-teal-600">{fmt(m.traffic ?? 0)}</p>
                <p className="text-[10px] text-slate-500">{es ? 'visitas orgánicas / mes' : 'organic visits / month'}</p>
                <p className="text-[10px] text-slate-400 mt-2">{es ? 'La evolución se irá mostrando con las próximas actualizaciones semanales.' : 'The trend will appear with the next weekly updates.'}</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <InfoBox testId="seo-box-backlinks" title="Backlinks" rows={[
            { label: 'Backlinks', value: m.backlinks },
            { label: es ? 'IPs de referencia' : 'Referring IPs', value: m.ref_ips },
            { label: es ? 'Subredes' : 'Subnets', value: m.ref_subnets },
            { label: es ? 'Enlaces .edu' : '.edu links', value: m.ref_edu },
            { label: es ? 'Enlaces .gov' : '.gov links', value: m.ref_gov },
            { label: es ? 'Idiomas' : 'Languages', value: m.ref_language, display: m.ref_language },
          ]} />
          <InfoBox testId="seo-box-positions" title={es ? 'Posiciones' : 'Positions'} rows={[
            { label: 'Top 3', value: m.top_3 },
            { label: 'Top 4-10', value: m.top_4_10 },
            { label: 'Top 11-20', value: m.top_11_20 },
            { label: 'Top 21-50', value: m.top_21_50 },
          ]} />
          <InfoBox testId="seo-box-ads" title={es ? 'Anuncios' : 'Ads'} rows={[
            { label: es ? 'Keywords de pago' : 'Paid keywords', value: m.paid_keywords },
            { label: es ? 'Anuncios' : 'Ads', value: m.ads_count },
            { label: es ? 'Tráfico de pago' : 'Paid traffic', value: m.paid_traffic },
          ]} />
          <InfoBox testId="seo-box-audience" title={es ? 'Tráfico y audiencia' : 'Traffic & audience'} rows={[
            { label: es ? 'Tráfico' : 'Traffic', value: m.traffic },
            { label: es ? 'Valor del tráfico' : 'Traffic value', value: m.traffic_value, display: has(m.traffic_value) ? `${fmt(m.traffic_value)} $` : null },
            { label: es ? 'País principal' : 'Main country', value: m.top_location || '—', display: m.top_location || '—' },
            { label: es ? 'Idioma' : 'Language', value: m.language, display: m.language },
          ]} />
        </div>
      </div>
    </div>
  );
};

export default SeoMetricsPanel;
